import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Roles } from '@tronche2tek/shared-types';
import type { ReactNode } from 'react';
import { Fragment, use } from 'react';
import { useTranslation } from 'react-i18next';

import { AbilityContext } from '../../../ability';
import { adminUsersQueryOptions } from '../../../api/admin';
import type { AdminMeDto } from '../../../api/generated';
import { meQueryOptions } from '../../../api/me';
import type { GraphUser } from '../../../api/users';
import { userQueryOptions } from '../../../api/users';
import { SingleCardGenerator } from '../../../components/cards/single-card/SingleCardGenerator';
import { Card } from '../../../components/common/Card';
import { PhotoManager } from '../../../components/photo/PhotoManager';
import { AuditActivityCard } from '../../../components/users/AuditActivityCard';
import { UserCapabilitiesCard } from '../../../components/users/UserCapabilitiesCard';
import { UserPageHeader } from '../../../components/users/UserPageHeader';

export const Route = createFileRoute('/_auth/users/$oid')({
  component: UserPage,
});

/**
 * One page per person, showing whichever cards the reader is entitled to.
 *
 * It replaces three — this one, `/me` and `/photos/me` — which rendered the same
 * components from the same endpoints under different titles, each numbering its
 * own cards, so together they produced `01`, `02`, `01`. Numbers are a position
 * in the list below, which is the only thing that knows what exists.
 *
 * Three cards, not seven. Identity moved into the header it was restating;
 * photo, upload and moderation state are one subject and one card; and the
 * statistics card is gone — it fetched a hundred audit rows to count two kinds
 * of action, for four numbers the history page already gives properly.
 */
function UserPage() {
  const { t } = useTranslation();
  const { oid } = Route.useParams();
  const ability = use(AbilityContext);
  const canReadAppUsers = ability.can('read', 'User');

  const { data: me } = useQuery(meQueryOptions());
  const { data: user, isLoading, error } = useQuery(userQueryOptions(oid));

  const isSelf = isOwnPage(me, oid);

  // The application role of somebody else is admin-only (`read:User`). Without
  // it the header drops the role rather than guessing.
  const { data: appUsersPage } = useQuery({
    ...adminUsersQueryOptions({ q: oid, limit: 10 }),
    enabled: !isSelf && canReadAppUsers,
  });

  if (isLoading) {
    return (
      <Card title={t('users.detail.loading')}>
        <p className="label cursor">LOADING</p>
      </Card>
    );
  }

  if (error || !user) {
    return (
      <Card title={t('users.detail.notFoundTitle')}>
        <p className="text-base">{t('users.detail.notFound')}</p>
      </Card>
    );
  }

  const displayName =
    // `||`, not `??`: both are required strings, but Entra can hold an empty one.
    user.displayName ||
    user.userPrincipalName ||
    t('users.detail.fallbackName');
  const selfRole = me?.role ?? null;
  const otherRole =
    appUsersPage?.users.find((u) => u.oid === oid)?.role ?? null;
  const role = isSelf ? selfRole : otherRole;

  return (
    <div>
      <UserPageHeader
        oid={oid}
        user={user}
        displayName={displayName}
        role={role}
      />
      <UserPageCards
        oid={oid}
        user={user}
        me={me}
        isSelf={isSelf}
        role={role}
      />
    </div>
  );
}

// A UPN reaches the same record as an OID — Graph accepts either — so a link
// built from one must not read as somebody else's page.
function isOwnPage(me: AdminMeDto | undefined, oid: string): boolean {
  return (
    !!me &&
    (me.oid === oid || (!!me.upn && me.upn.toLowerCase() === oid.toLowerCase()))
  );
}

function UserPageCards({
  oid,
  user,
  me,
  isSelf,
  role,
}: {
  oid: string;
  user: GraphUser;
  me: AdminMeDto | undefined;
  isSelf: boolean;
  role: string | null;
}) {
  const { t } = useTranslation();
  const ability = use(AbilityContext);

  const canGenerateCards = ability.can('generate', 'Card');
  const canReadAudit = ability.can('read', 'Audit');
  const canModerate = ability.can('moderate', 'Photo');
  // Role `user` does not hold this one, and `DELETE /users/:oid/photo`
  // requires it — so the card must not offer a button the server will refuse.
  const canDelete = ability.can('delete', 'Photo');
  const isAdmin = me?.role === Roles.ADMIN;

  // Each entry owns a stable key so a card appearing or disappearing does not
  // remount its neighbours, and takes its number from its position.
  const cards: { key: string; render: (n: number) => ReactNode }[] = [];

  cards.push({
    key: 'photo',
    render: (n) => (
      <PhotoManager
        num={n}
        oid={oid}
        user={user}
        isSelf={isSelf}
        actorOid={me?.oid ?? null}
        canModerate={canModerate}
        canDelete={canDelete}
        isAdmin={isAdmin}
      />
    ),
  });

  if (canGenerateCards) {
    cards.push({
      key: 'card-generation',
      render: (n) => (
        <Card
          num={n}
          title={t('users.detail.card.title')}
          meta={t('users.detail.card.meta')}
        >
          <SingleCardGenerator user={user} />
        </Card>
      ),
    });
  }

  if (canReadAudit) {
    cards.push({
      key: 'activity',
      render: (n) => (
        <AuditActivityCard
          num={n}
          oid={oid}
          mode={isSelf ? 'actor' : 'target'}
        />
      ),
    });
  }

  // Last, and only for an admin — on anybody's page, their own included. The
  // list is what a role is allowed to do, which is administrative reference
  // material: a reader who cannot change anyone's role has nothing to do with
  // it, and reading their own entry only tells them what the interface already
  // shows them by offering or withholding each action.
  if (isAdmin && role) {
    cards.push({
      key: 'capabilities',
      render: (n) => <UserCapabilitiesCard num={n} role={role} />,
    });
  }

  // One column while a card would be narrower than 36rem, two (or more,
  // on a wider shell) once they fit — `auto-fit` decides from the space
  // there is, so nothing here names a breakpoint. `min(100%, …)` is what
  // keeps the track from exceeding the viewport on a phone, where 36rem
  // is wider than the screen. `items-start` so a short card does not
  // stretch to its row neighbour's height.
  //
  // 36rem is not a round number: `html` sets `font-size: 14px`, so a rem
  // is 14px here and 36rem is 504px — a track wide enough that the photo
  // card's own 32rem (448px) container query still matches inside it,
  // padding deducted. At 30rem the two split in opposite directions: the
  // cards paired up at 1024px and the photo card, left with 415px, went
  // back to stacking its halves and grew to 668px tall.
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,36rem),1fr))] items-start gap-6">
      {cards.map(({ key, render }, i) => (
        <Fragment key={key}>{render(i + 1)}</Fragment>
      ))}
    </div>
  );
}
